import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { formatCurrency, formatDate, formatPeriod, currentPeriod, periodOptions } from "@/lib/format";
import StatusPill from "@/components/StatusPill";
import { Modal, Field } from "@/pages/Properties";
import DeletePropertyModal from "@/components/DeletePropertyModal";
import IncreaseNoticeModal from "@/components/IncreaseNoticeModal";
import MoveOutModal from "@/components/MoveOutModal";
import PaymentTxnModal from "@/components/PaymentTxnModal";
import DeleteTenantModal from "@/components/DeleteTenantModal";
import ElectricityFormModal from "@/components/ElectricityFormModal";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Plus, Trash2, Mail, Phone, CheckCircle2, BellRing,
  Users, FileText, ListChecks, LayoutGrid, Wallet, PiggyBank, Receipt, Percent,
  AlertCircle, TrendingUp, CalendarClock, Calendar, Building2, LogOut, FileDown,
  Loader2, History, Home, Ruler, Pencil, Zap
} from "lucide-react";
const inputCls = "w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 text-sm";

const SECTIONS = ["overview", "rent-roll", "units", "electricity", "contracts", "tenants", "reminders"];

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab = SECTIONS.includes(tabParam) ? tabParam : "overview";
  const setActiveTab = (t) => setSearchParams({ tab: t });

  const [property, setProperty] = useState(null);
  const [dash, setDash] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [queue, setQueue] = useState({ overdue: [], expiring_contracts: [], upcoming_increases: [] });

  const [period, setPeriod] = useState(currentPeriod());
  const [showContractForm, setShowContractForm] = useState(false);
  const [showTenantForm, setShowTenantForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [increaseTenant, setIncreaseTenant] = useState(null);
  const [moveOutTarget, setMoveOutTarget] = useState(null); // { tenant, currentBalance }
  const [txnPayment, setTxnPayment] = useState(null); // payment row for txn modal
  const [deposits, setDeposits] = useState([]);
  const [deleteTenantTarget, setDeleteTenantTarget] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitFormTarget, setUnitFormTarget] = useState(null); // null | {} (new) | {unit} (edit)
  const [electricity, setElectricity] = useState([]);
  const [electricitySummary, setElectricitySummary] = useState(null);
  const [electricityFormOpen, setElectricityFormOpen] = useState(false);

  // Load deposits so Tenants tab can show balance + deposit_holder inline
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/deposits?property_id=${id}`);
        setDeposits(data);
      } catch {}
    })();
  }, [id, contracts.length]);

  const loadAll = async () => {
    try {
      const [p, d, c, t, q, u, e, es] = await Promise.all([
        api.get(`/properties/${id}`),
        api.get(`/dashboard?property_id=${id}&period=${period}`),
        api.get(`/contracts?property_id=${id}`),
        api.get(`/tenants`),
        api.get(`/reminders/queue?property_id=${id}`),
        api.get(`/units?property_id=${id}`),
        api.get(`/electricity?property_id=${id}`),
        api.get(`/electricity/summary?property_id=${id}`),
      ]);
      setProperty(p.data);
      setDash(d.data);
      setContracts(c.data);
      setTenants(t.data);
      setQueue(q.data);
      setUnits(u.data);
      setElectricity(e.data);
      setElectricitySummary(es.data);
    } catch (e) {
      toast.error("Could not load property");
      navigate("/");
    }
  };

  const loadPayments = async () => {
    const { data } = await api.get(`/payments?property_id=${id}&period=${period}`);
    setPayments(data);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { loadPayments(); /* eslint-disable-next-line */ }, [id, period]);
  useEffect(() => {
    (async () => {
      if (!property) return;
      const { data } = await api.get(`/dashboard?property_id=${id}&period=${period}`);
      setDash(data);
    })();
    // eslint-disable-next-line
  }, [period]);

  const propertyTenants = useMemo(() => {
    const map = new Map();
    for (const c of contracts) {
      const tid = c.tenant?.id || c.tenant_id;
      if (!tid || map.has(tid)) continue;
      const t = c.tenant?.id ? c.tenant : tenants.find((x) => x.id === tid);
      if (t) map.set(tid, { ...t, contract: c });
    }
    return Array.from(map.values());
  }, [contracts, tenants]);

  const delProperty = () => setShowDeleteModal(true);
  const confirmDeleteProperty = async () => {
    await api.delete(`/properties/${id}`);
    toast.success("Property deleted");
    navigate("/");
  };

  const markPaid = async (p) => {
    try {
      await api.put(`/payments/${p.id}`, {
        status: "paid",
        amount_paid: p.gross,
        paid_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Marked paid");
      loadPayments();
      loadAll();
    } catch { toast.error("Failed"); }
  };
  const markUnpaid = async (p) => {
    await api.put(`/payments/${p.id}`, { status: "unpaid", amount_paid: 0, paid_date: null });
    loadPayments();
    loadAll();
  };
  // Record a payment inline against a rent-roll row (single manual entry)
  const savePayment = async ({ tenant_id, amount, date_received, allocated_period }) => {
    await api.post("/transactions", {
      tenant_id,
      amount: Number(amount),
      date_received,
      allocated_period,
      notes: "",
    });
    toast.success(`${formatCurrency(amount)} recorded`);
    await loadPayments();
    await loadAll();
  };

  const sendReminder = async (payment_id) => {
    try {
      const { data } = await api.post("/reminders/send", { payment_id });
      if (data.status === "sent") toast.success("Reminder emailed");
      else if (data.status === "queued") toast.message("Reminder logged (add RESEND_API_KEY to send live email)");
      else toast.error("Reminder failed");
    } catch { toast.error("Could not send"); }
  };

  // Sidebar navigation for property-scoped context
  const navItems = property
    ? [
        {
          onClick: () => navigate("/"),
          label: "All properties",
          icon: ArrowLeft,
          testid: "nav-back-to-properties",
          active: false,
        },
        { onClick: () => setActiveTab("overview"), label: "Overview", icon: LayoutGrid, testid: "pd-nav-overview", active: activeTab === "overview" },
        { onClick: () => setActiveTab("rent-roll"), label: "Rent Roll", icon: ListChecks, testid: "pd-nav-rentroll", active: activeTab === "rent-roll" },
        { onClick: () => setActiveTab("units"), label: "Units", icon: Home, testid: "pd-nav-units", active: activeTab === "units" },
        { onClick: () => setActiveTab("electricity"), label: "Electricity", icon: Zap, testid: "pd-nav-electricity", active: activeTab === "electricity" },
        { onClick: () => setActiveTab("tenants"), label: "Tenants", icon: Users, testid: "pd-nav-tenants", active: activeTab === "tenants" },
        { onClick: () => setActiveTab("reminders"), label: "Reminders", icon: BellRing, testid: "pd-nav-reminders", active: activeTab === "reminders" },
      ]
    : [];

  const sidebarHeader = property && (
    <div className="px-6 py-4 border-b border-slate-200" data-testid="sidebar-property-header">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Property</div>
      <div className="flex items-start gap-2">
        <Building2 className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <div className="font-display font-semibold tracking-tight text-slate-900 truncate">{property.name}</div>
          <div className="text-xs text-slate-500 truncate">{property.address}</div>
        </div>
      </div>
    </div>
  );

  if (!property) {
    return (
      <Layout title="Loading…">
        <div className="text-sm text-slate-500">Fetching property…</div>
      </Layout>
    );
  }

  return (
    <Layout
      title={property.name}
      subtitle={property.address}
      navItems={navItems}
      sidebarHeader={sidebarHeader}
      actions={
        <button onClick={delProperty} data-testid="property-detail-delete" className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg border border-red-200 text-red-700 text-sm font-medium hover:bg-red-50 transition">
          <Trash2 className="w-4 h-4" /> Delete
        </button>
      }
    >
      {activeTab === "overview" && (
        <OverviewSection
          property={property}
          dash={dash}
          period={period}
          setPeriod={setPeriod}
        />
      )}

      {activeTab === "rent-roll" && (
        <RentRollSection
          payments={payments}
          period={period}
          setPeriod={setPeriod}
          markPaid={markPaid}
          markUnpaid={markUnpaid}
          sendReminder={sendReminder}
          propertyId={id}
          onRecordPayment={setTxnPayment}
          savePayment={savePayment}
        />
      )}

      {activeTab === "contracts" && (
        <ContractsSection
          contracts={contracts}
          onAdd={() => setShowContractForm(true)}
          canAdd={tenants.length > 0}
          onDelete={async (cid) => {
            if (!window.confirm("Delete this contract?")) return;
            await api.delete(`/contracts/${cid}`);
            toast.success("Contract deleted");
            loadAll();
            loadPayments();
          }}
        />
      )}

      {activeTab === "tenants" && (
        <TenantsSection
          propertyTenants={propertyTenants}
          deposits={deposits}
          units={units}
          onAdd={() => setShowTenantForm(true)}
          onMoveOut={(t, bal) => setMoveOutTarget({ tenant: t, currentBalance: bal })}
          onDelete={(t) => setDeleteTenantTarget(t)}
        />
      )}

      {activeTab === "units" && (
        <UnitsSection
          units={units}
          propertyTenants={propertyTenants}
          allTenants={tenants.filter((t) => t.property_id === id)}
          onAdd={() => setUnitFormTarget({})}
          onEdit={(u) => setUnitFormTarget({ unit: u })}
          onDelete={async (u) => {
            if (!window.confirm(`Delete unit "${u.name}"? Any tenant on this unit becomes unassigned.`)) return;
            try {
              await api.delete(`/units/${u.id}`);
              toast.success("Unit deleted");
              await loadAll();
            } catch { toast.error("Could not delete unit"); }
          }}
        />
      )}

      {activeTab === "electricity" && (
        <ElectricitySection
          units={units}
          entries={electricity}
          summary={electricitySummary}
          onAdd={() => setElectricityFormOpen(true)}
          onDelete={async (eid) => {
            if (!window.confirm("Remove this electricity entry?")) return;
            try {
              await api.delete(`/electricity/${eid}`);
              toast.success("Entry removed");
              await loadAll();
            } catch { toast.error("Could not remove"); }
          }}
        />
      )}

      {activeTab === "reminders" && (
        <RemindersSection queue={queue} sendReminder={sendReminder} onOpenIncrease={setIncreaseTenant} />
      )}

      {showContractForm && (
        <ContractFormModal
          onClose={() => setShowContractForm(false)}
          propertyId={id}
          tenants={tenants}
          units={units}
          onSaved={() => { setShowContractForm(false); loadAll(); loadPayments(); setActiveTab("rent-roll"); }}
        />
      )}
      {showTenantForm && (
        <TenantFormModal
          onClose={() => setShowTenantForm(false)}
          onSaved={async () => {
            setShowTenantForm(false);
            await loadAll();
            toast.success("Tenant added — now create their contract");
            setShowContractForm(true);
          }}
        />
      )}
      {showDeleteModal && (
        <DeletePropertyModal
          property={property}
          onCancel={() => setShowDeleteModal(false)}
          onConfirmDelete={confirmDeleteProperty}
        />
      )}
      {increaseTenant && (
        <IncreaseNoticeModal
          tenant={increaseTenant}
          onClose={() => setIncreaseTenant(null)}
          onApplied={() => { loadAll(); loadPayments(); }}
        />
      )}
      {moveOutTarget && (
        <MoveOutModal
          tenant={moveOutTarget.tenant}
          currentBalance={moveOutTarget.currentBalance}
          onClose={() => setMoveOutTarget(null)}
          onDone={() => { loadAll(); loadPayments(); }}
        />
      )}
      {txnPayment && (
        <PaymentTxnModal
          payment={txnPayment}
          onClose={() => setTxnPayment(null)}
          onSaved={() => { loadPayments(); loadAll(); }}
        />
      )}
      {deleteTenantTarget && (
        <DeleteTenantModal
          tenant={deleteTenantTarget}
          onCancel={() => setDeleteTenantTarget(null)}
          onConfirmDelete={async () => {
            try {
              await api.delete(`/tenants/${deleteTenantTarget.id}`);
              toast.success("Tenant deleted");
              setDeleteTenantTarget(null);
              await loadAll();
              await loadPayments();
            } catch { toast.error("Could not delete tenant"); }
          }}
        />
      )}
      {unitFormTarget && (
        <UnitFormModal
          propertyId={id}
          initial={unitFormTarget.unit}
          onClose={() => setUnitFormTarget(null)}
          onSaved={async () => { setUnitFormTarget(null); await loadAll(); }}
        />
      )}
      {electricityFormOpen && (
        <ElectricityFormModal
          propertyId={id}
          units={units}
          tenants={tenants.filter((t) => t.property_id === id)}
          defaultType="recovery"
          onClose={() => setElectricityFormOpen(false)}
          onSaved={async () => { setElectricityFormOpen(false); await loadAll(); }}
        />
      )}
    </Layout>
  );
}

/* ============================================================================
 * SECTION: Overview
 * ============================================================================ */
function OverviewSection({ property, dash, period, setPeriod }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 card-flat overflow-hidden fade-up">
        {property.image_url && (
          <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
            <img src={property.image_url} alt={property.name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Address</div>
          <div className="text-sm text-slate-700 flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
            <span>{property.address}</span>
          </div>
          {property.notes && (
            <>
              <div className="text-xs uppercase tracking-widest text-slate-400 mt-4 mb-1">Notes</div>
              <p className="text-sm text-slate-700">{property.notes}</p>
            </>
          )}
        </div>
      </div>

      <div className="lg:col-span-2 grid grid-cols-2 gap-4 content-start">
        <StatCard label="Gross rental" value={formatCurrency(dash?.gross_rental)} icon={Wallet} testid="pd-metric-gross" />
        <StatCard label="Agent commission" value={formatCurrency(dash?.agent_commission)} icon={Percent} tone="amber" testid="pd-metric-commission" />
        <StatCard label="Taxes" value={formatCurrency(dash?.taxes)} icon={Receipt} tone="indigo" testid="pd-metric-taxes" />
        <StatCard label="Nett rent" value={formatCurrency(dash?.nett_rent)} icon={PiggyBank} tone="green" testid="pd-metric-nett" />
        <div className="col-span-2 card-flat p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Period</div>
            <div className="font-display text-xl font-semibold">{formatPeriod(period)}</div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Collected</div>
              <div className="font-display font-semibold text-emerald-700">{formatCurrency(dash?.collected)}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Outstanding</div>
              <div className="font-display font-semibold text-red-700">{formatCurrency(dash?.outstanding)}</div>
            </div>
            <select
              value={period} onChange={(e) => setPeriod(e.target.value)}
              data-testid="pd-period-select"
              className="h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              {periodOptions().map((p) => <option key={p} value={p}>{formatPeriod(p)}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * SECTION: Rent Roll
 * ============================================================================ */
function RentRollSection({ payments, period, setPeriod, markPaid, markUnpaid, sendReminder, propertyId, onRecordPayment, savePayment }) {
  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("roost_token");
      const res = await fetch(`${(process.env.REACT_APP_BACKEND_URL)}/api/rent-roll/export.csv?property_id=${propertyId}&period=${period}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("CSV error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rent-roll-${period}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rent roll exported");
    } catch { toast.error("Could not export CSV"); }
  };
  return (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Rent Roll</div>
          <div className="font-display text-xl font-semibold mt-0.5">{formatPeriod(period)}</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            data-testid="pd-rentroll-export-csv"
            className="h-10 px-3.5 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50 transition inline-flex items-center gap-1.5"
          >
            <FileDown className="w-4 h-4" /> Export CSV
          </button>
          <select
            value={period} onChange={(e) => setPeriod(e.target.value)}
            data-testid="pd-rentroll-period-select"
            className="h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          >
            {periodOptions().map((p) => <option key={p} value={p}>{formatPeriod(p)}</option>)}
          </select>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="card-flat p-8 text-center">
          <div className="text-sm text-slate-500">No payments generated for {formatPeriod(period)}. Add a contract to populate the rent roll.</div>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto fade-up">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Tenant</th>
                <th className="text-left px-4 py-3 font-medium">Due</th>
                <th className="text-right px-4 py-3 font-medium">Gross</th>
                <th className="text-right px-4 py-3 font-medium">Nett</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium w-[340px]">Record payment</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((p) => (
                <RentRollRow
                  key={p.id}
                  p={p}
                  savePayment={savePayment}
                  sendReminder={sendReminder}
                  onOpenHistory={onRecordPayment}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function RentRollRow({ p, savePayment, sendReminder, onOpenHistory }) {
  const [amount, setAmount] = useState(String(Math.max(0, Number(p.gross || 0) - Number(p.amount_paid || 0))));
  const [dateReceived, setDateReceived] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const isPaid = p.status === "paid";
  const overdue = !isPaid && p.period < currentPeriod();
  const statusLabel = isPaid ? "paid" : (p.status === "partial" ? "partial" : (overdue ? "overdue" : "unpaid"));

  const submit = async (e) => {
    e.preventDefault();
    const n = Number(amount);
    if (!n || n <= 0) { toast.error("Enter an amount received"); return; }
    setBusy(true);
    try {
      await savePayment({
        tenant_id: p.tenant_id,
        amount: n,
        date_received: dateReceived,
        allocated_period: p.period,
      });
      // After save, seed the amount field with the (new) remaining if any
      setAmount("");
    } catch {
      toast.error("Could not record payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className={`hover:bg-slate-50/50 ${overdue ? "bg-red-50/30" : ""}`} data-testid={`pd-payment-row-${p.id}`}>
      <td className="px-4 py-3 font-medium">{p.tenant_name}</td>
      <td className="px-4 py-3 text-slate-700">{formatDate(p.due_date)}</td>
      <td className="px-4 py-3 text-right font-mono-plex">{formatCurrency(p.gross)}</td>
      <td className="px-4 py-3 text-right font-mono-plex font-medium">{formatCurrency(p.net)}</td>
      <td className="px-4 py-3">
        <StatusPill status={statusLabel} />
        {Number(p.amount_paid) > 0 && !isPaid && (
          <div className="text-[10px] text-slate-500 mt-0.5 font-mono-plex" data-testid={`pd-payment-progress-${p.id}`}>
            {formatCurrency(p.amount_paid)} / {formatCurrency(p.gross)}
          </div>
        )}
        {isPaid && p.paid_date && (
          <div className="text-[10px] text-slate-500 mt-0.5 font-mono-plex">on {formatDate(p.paid_date)}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <form onSubmit={submit} className="flex items-center gap-1.5" data-testid={`pd-inline-record-form-${p.id}`}>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 font-mono-plex">R</span>
            <input
              type="number" step="0.01" min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid={`pd-inline-amount-${p.id}`}
              className="w-24 h-9 pl-5 pr-2 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 text-xs font-mono-plex"
            />
          </div>
          <input
            type="date"
            value={dateReceived}
            onChange={(e) => setDateReceived(e.target.value)}
            data-testid={`pd-inline-date-${p.id}`}
            className="h-9 px-2 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 text-xs font-mono-plex"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid={`pd-inline-save-${p.id}`}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Save
          </button>
        </form>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          <button
            onClick={() => onOpenHistory(p)}
            data-testid={`pd-payment-history-${p.id}`}
            title="Payment history"
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50"
          >
            <History className="w-3 h-3" /> History
          </button>
          {!isPaid && (
            <button
              onClick={() => sendReminder(p.id)}
              data-testid={`pd-payment-remind-${p.id}`}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50"
            >
              <BellRing className="w-3 h-3" /> Remind
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ============================================================================
 * SECTION: Contracts
 * ============================================================================ */
function ContractsSection({ contracts, onAdd, canAdd, onDelete }) {
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Leases</div>
          <div className="font-display text-xl font-semibold mt-0.5">{contracts.length} contract{contracts.length !== 1 ? "s" : ""}</div>
        </div>
        <button
          onClick={onAdd}
          disabled={!canAdd}
          data-testid="pd-add-contract-btn"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition disabled:opacity-40"
        >
          <Plus className="w-4 h-4" /> Add contract
        </button>
      </div>
      {contracts.length === 0 ? (
        <div className="card-flat p-8 text-center">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold">No leases yet</div>
          <p className="text-sm text-slate-500 mt-1">
            {canAdd ? "Start your first lease. RoostHQ will build the rent roll automatically." : "Add a tenant first — then create a contract."}
          </p>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto fade-up">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Tenant · Unit</th>
                <th className="text-left px-6 py-3 font-medium">Term</th>
                <th className="text-right px-6 py-3 font-medium">Rent</th>
                <th className="text-left px-6 py-3 font-medium">Next increase</th>
                <th className="text-left px-6 py-3 font-medium">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {contracts.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50/50" data-testid={`pd-contract-row-${c.id}`}>
                  <td className="px-6 py-3.5">
                    <div className="font-medium">{c.tenant?.full_name || "—"}</div>
                    {c.unit_label && <div className="text-xs text-slate-500 mt-0.5">{c.unit_label}</div>}
                  </td>
                  <td className="px-6 py-3.5 text-slate-700">
                    <div className="flex items-center gap-1.5 text-xs">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      {formatDate(c.start_date)} → {formatDate(c.end_date)}
                    </div>
                  </td>
                  <td className="px-6 py-3.5 text-right font-mono-plex font-medium">{formatCurrency(c.monthly_rent)}</td>
                  <td className="px-6 py-3.5 text-slate-700">
                    {c.next_increase_date ? (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <TrendingUp className="w-3 h-3 text-indigo-600" />
                        {formatDate(c.next_increase_date)} · +{c.increase_percent || 0}%
                      </span>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-6 py-3.5"><StatusPill status={c.status || "active"} /></td>
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={() => onDelete(c.id)}
                      data-testid={`pd-contract-delete-${c.id}`}
                      className="text-slate-400 hover:text-red-600 transition p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ============================================================================
 * SECTION: Tenants
 * ============================================================================ */
function TenantsSection({ propertyTenants, deposits, units, onAdd, onMoveOut, onDelete }) {
  const balanceFor = (tid) => {
    const d = (deposits || []).find((x) => x.contract_id === tid || x.tenant_id === tid);
    return d ? Number(d.balance || 0) : 0;
  };
  const unitLabelFor = (t) => {
    if (t.unit_id) {
      const u = (units || []).find((x) => x.id === t.unit_id);
      if (u) return u.name;
    }
    return t.unit_label || "";
  };
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Tenants on this property</div>
          <div className="font-display text-xl font-semibold mt-0.5">{propertyTenants.length} tenant{propertyTenants.length !== 1 ? "s" : ""}</div>
        </div>
        <button
          onClick={onAdd}
          data-testid="pd-add-tenant-btn"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition"
        >
          <Plus className="w-4 h-4" /> Add tenant
        </button>
      </div>
      {propertyTenants.length === 0 ? (
        <div className="card-flat p-8 text-center">
          <Users className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold">No tenants on this property yet</div>
          <p className="text-sm text-slate-500 mt-1">Add a tenant to start collecting rent.</p>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto fade-up">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Name · Unit</th>
                <th className="text-left px-6 py-3 font-medium">Contact</th>
                <th className="text-left px-6 py-3 font-medium">Lease</th>
                <th className="text-right px-6 py-3 font-medium">Deposit</th>
                <th className="text-left px-6 py-3 font-medium">Held with</th>
                <th className="text-left px-6 py-3 font-medium">Status</th>
                <th className="text-right px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {propertyTenants.map((t) => {
                const bal = balanceFor(t.id);
                const initial = Number(t.deposit || 0);
                const ended = t.status === "ended" || t.archived;
                return (
                  <tr key={t.id} className={`hover:bg-slate-50/50 ${ended ? "opacity-60" : ""}`} data-testid={`pd-tenant-row-${t.id}`}>
                    <td className="px-6 py-3.5">
                      <div className="font-medium">{t.full_name}</div>
                      {unitLabelFor(t) && <div className="text-xs text-slate-500 mt-0.5">{unitLabelFor(t)}</div>}
                    </td>
                    <td className="px-6 py-3.5 text-slate-700">
                      <div className="flex flex-col gap-0.5 text-xs">
                        {t.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" />{t.email}</span>}
                        {t.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{t.phone}</span>}
                        {!t.email && !t.phone && <span className="text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-slate-700 text-xs">
                      {t.start_date ? (
                        <>
                          {formatDate(t.start_date)} → {formatDate(t.end_date)}
                          <div className="text-slate-500 mt-0.5">{formatCurrency(t.monthly_rent)} /mo</div>
                        </>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-6 py-3.5 text-right font-mono-plex">
                      <div className="font-medium">{formatCurrency(bal)}</div>
                      {initial !== bal && <div className="text-[10px] text-slate-400 mt-0.5">Initial {formatCurrency(initial)}</div>}
                    </td>
                    <td className="px-6 py-3.5 text-slate-700 text-xs">{t.deposit_holder || <span className="text-slate-400">—</span>}</td>
                    <td className="px-6 py-3.5"><StatusPill status={ended ? "ended" : "active"} /></td>
                    <td className="px-6 py-3.5 text-right">
                      <div className="inline-flex items-center gap-1.5">
                      {ended ? (
                        <button
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem("roost_token");
                              const res = await fetch(`${(process.env.REACT_APP_BACKEND_URL)}/api/tenants/${t.id}/move-out-report`, {
                                credentials: "include",
                                headers: token ? { Authorization: `Bearer ${token}` } : {},
                              });
                              if (!res.ok) throw new Error();
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `move-out-report-${t.full_name.replace(/\s+/g, "_")}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              URL.revokeObjectURL(url);
                              toast.success("Move-out report downloaded");
                            } catch { toast.error("Could not download report"); }
                          }}
                          data-testid={`pd-move-out-report-${t.id}`}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 transition"
                        >
                          <FileDown className="w-3 h-3" /> Report PDF
                        </button>
                      ) : (
                        <button
                          onClick={() => onMoveOut(t, bal)}
                          data-testid={`pd-move-out-${t.id}`}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 transition"
                        >
                          <LogOut className="w-3 h-3" /> Move out
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(t)}
                        data-testid={`pd-delete-tenant-${t.id}`}
                        title="Delete tenant"
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50 transition"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ============================================================================
 * SECTION: Reminders
 * ============================================================================ */
function RemindersSection({ queue, sendReminder, onOpenIncrease }) {
  return (
    <div className="space-y-6">
      <ReminderCard title="Overdue payments" icon={AlertCircle} tone="red" testid="pd-section-overdue">
        {queue.overdue.length === 0 ? <Empty text="Nobody's overdue on this property." /> : (
          <ul className="divide-y divide-slate-100">
            {queue.overdue.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-4 py-3.5" data-testid={`pd-overdue-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.tenant?.full_name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{formatPeriod(p.period)} · Due {formatDate(p.due_date)} · {formatCurrency(p.gross)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status="overdue" />
                  <button onClick={() => sendReminder(p.id)} data-testid={`pd-overdue-send-${p.id}`} className="inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition">
                    <BellRing className="w-3.5 h-3.5" /> Send reminder
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </ReminderCard>

      <ReminderCard title="Upcoming rent increases" icon={TrendingUp} tone="indigo" testid="pd-section-increases">
        {queue.upcoming_increases.length === 0 ? <Empty text="No rent increases scheduled in the next 60 days." /> : (
          <ul className="divide-y divide-slate-100">
            {queue.upcoming_increases.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-4 py-3.5" data-testid={`pd-increase-${c.id}`}>
                <div>
                  <div className="font-medium">{c.tenant?.full_name || c.full_name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Effective {formatDate(c.next_increase_date)} · +{c.increase_percent || 0}% · Current {formatCurrency(c.monthly_rent)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status="upcoming_increase" />
                  <button
                    onClick={() => onOpenIncrease({
                      id: c.id,
                      full_name: c.tenant?.full_name || c.full_name || "",
                      email: c.tenant?.email || c.email || "",
                      monthly_rent: c.monthly_rent,
                      increase_percent: c.increase_percent,
                      next_increase_date: c.next_increase_date,
                    })}
                    data-testid={`pd-increase-notice-${c.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition"
                  >
                    <TrendingUp className="w-3.5 h-3.5" /> Notice & apply
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </ReminderCard>

      <ReminderCard title="Expiring contracts" icon={CalendarClock} tone="amber" testid="pd-section-expiring">
        {queue.expiring_contracts.length === 0 ? <Empty text="No contracts ending in the next 60 days." /> : (
          <ul className="divide-y divide-slate-100">
            {queue.expiring_contracts.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-4 py-3.5">
                <div>
                  <div className="font-medium">{c.tenant?.full_name || c.full_name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Ends {formatDate(c.end_date)} · {formatCurrency(c.monthly_rent)} /mo</div>
                </div>
                <StatusPill status="due_soon" label="Expiring" />
              </li>
            ))}
          </ul>
        )}
      </ReminderCard>
    </div>
  );
}

/* ============================================================================
 * Reusable pieces
 * ============================================================================ */
function StatCard({ label, value, icon: Icon, tone = "default", testid }) {
  const toneMap = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    indigo: "bg-indigo-50 text-indigo-700",
  };
  return (
    <div className="card-flat p-5 fade-up" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-8 h-8 rounded-lg grid place-items-center ${toneMap[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      </div>
      <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function ReminderCard({ title, icon: Icon, tone, children, testid }) {
  const toneMap = {
    red: "bg-red-50 text-red-700",
    amber: "bg-amber-50 text-amber-700",
    indigo: "bg-indigo-50 text-indigo-700",
  };
  return (
    <div className="card-flat p-6 fade-up" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg grid place-items-center ${toneMap[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Empty({ text }) { return <div className="text-sm text-slate-500 py-2">{text}</div>; }

/* ============================================================================
 * MODALS
 * ============================================================================ */
function ContractFormModal({ onClose, propertyId, tenants, units, onSaved }) {
  const [form, setForm] = useState({
    tenant_id: "", property_id: propertyId, unit_id: "", unit_label: "",
    start_date: "", end_date: "",
    monthly_rent: 0, commission_percent: 0, tax_percent: 0,
    increase_percent: 0, next_increase_date: "",
    deposit: 0, payment_day: 1, notes: "",
  });

  const availableUnits = units || [];
  // Auto-fill monthly rent from unit's preferred_rate when a unit is selected
  const pickUnit = (uid) => {
    const u = availableUnits.find((x) => x.id === uid);
    setForm((f) => ({
      ...f,
      unit_id: uid,
      unit_label: u ? u.name : f.unit_label,
      monthly_rent: u && u.preferred_rate ? u.preferred_rate : f.monthly_rent,
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contracts", {
        ...form,
        unit_id: form.unit_id || null,
        monthly_rent: parseFloat(form.monthly_rent) || 0,
        commission_percent: parseFloat(form.commission_percent) || 0,
        tax_percent: parseFloat(form.tax_percent) || 0,
        increase_percent: parseFloat(form.increase_percent) || 0,
        deposit: parseFloat(form.deposit) || 0,
        payment_day: parseInt(form.payment_day) || 1,
        next_increase_date: form.next_increase_date || null,
      });
      toast.success("Contract created — rent roll generated");
      onSaved();
    } catch { toast.error("Could not save contract"); }
  };

  return (
    <Modal onClose={onClose} title="Allocate tenant to a unit">
      <form onSubmit={save} className="space-y-4" data-testid="pd-contract-form">
        <Field label="Tenant">
          <select required value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} data-testid="pd-contract-tenant-select" className={inputCls}>
            <option value="">Select tenant…</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </select>
        </Field>
        {availableUnits.length > 0 ? (
          <Field label="Unit">
            <select value={form.unit_id} onChange={(e) => pickUnit(e.target.value)} data-testid="pd-contract-unit-select" className={inputCls}>
              <option value="">Select a unit…</option>
              {availableUnits.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.size_sqm ? ` · ${u.size_sqm} m²` : ""}{u.preferred_rate ? ` · R ${Number(u.preferred_rate).toLocaleString()}/mo` : ""}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Unit (optional label)">
            <input value={form.unit_label} onChange={(e) => setForm({ ...form, unit_label: e.target.value })} className={inputCls} placeholder="Unit 3B — add units in the Units tab to pick from a list" />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Start date">
            <input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="pd-contract-start" className={inputCls} />
          </Field>
          <Field label="End date">
            <input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="pd-contract-end" className={inputCls} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Monthly rent (R)">
            <input type="number" step="0.01" required value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} data-testid="pd-contract-rent" className={inputCls} />
          </Field>
          <Field label="Commission %">
            <input type="number" step="0.1" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Tax %">
            <input type="number" step="0.1" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Increase %">
            <input type="number" step="0.1" value={form.increase_percent} onChange={(e) => setForm({ ...form, increase_percent: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Next increase">
            <input type="date" value={form.next_increase_date} onChange={(e) => setForm({ ...form, next_increase_date: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Deposit (R)">
            <input type="number" step="0.01" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <Field label="Rent due on day (of month)">
          <input type="number" min="1" max="28" value={form.payment_day} onChange={(e) => setForm({ ...form, payment_day: e.target.value })} className={inputCls} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancel</button>
          <button type="submit" data-testid="pd-contract-save" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Save contract</button>
        </div>
      </form>
    </Modal>
  );
}

function UnitsSection({ units, propertyTenants, allTenants, onAdd, onEdit, onDelete }) {
  const tenantFor = (uid) => (propertyTenants || []).find(
    (t) => t.unit_id === uid && !(t.status === "ended" || t.archived)
  );
  const historyFor = (uid) => {
    const list = (allTenants || []).filter((t) => t.unit_id === uid);
    // Sort by start_date desc (most recent first)
    return list.slice().sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
  };
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Units on this property</div>
          <div className="font-display text-xl font-semibold mt-0.5">
            {units.length} unit{units.length !== 1 ? "s" : ""}
            {units.length > 0 && (
              <span className="ml-2 text-sm text-slate-500 font-normal font-mono-plex">
                · {units.filter((u) => tenantFor(u.id)).length} occupied · {units.filter((u) => !tenantFor(u.id)).length} vacant
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onAdd}
          data-testid="pd-add-unit-btn"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition"
        >
          <Plus className="w-4 h-4" /> Add unit
        </button>
      </div>

      {units.length === 0 ? (
        <div className="card-flat p-8 text-center">
          <Home className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold">No units yet</div>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            A unit is the income-producing space on this property. Add units first, then allocate tenants to them.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {units.map((u) => {
            const t = tenantFor(u.id);
            const occupied = !!t;
            const history = historyFor(u.id);
            return (
              <div
                key={u.id}
                data-testid={`pd-unit-card-${u.id}`}
                className="card-flat p-5 flex flex-col gap-4 fade-up"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-slate-900 text-white grid place-items-center">
                        <Home className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-lg truncate">{u.name}</div>
                        <StatusPill status={occupied ? "active" : "unpaid"} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onEdit(u)}
                      data-testid={`pd-unit-edit-${u.id}`}
                      title="Edit unit"
                      className="w-8 h-8 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 grid place-items-center"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(u)}
                      data-testid={`pd-unit-delete-${u.id}`}
                      title="Delete unit"
                      className="w-8 h-8 rounded-md border border-red-200 text-red-600 hover:bg-red-50 grid place-items-center"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-slate-200 p-3">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
                      <Ruler className="w-3 h-3" /> Size
                    </div>
                    <div className="font-mono-plex text-sm font-semibold text-slate-900 mt-1">
                      {u.size_sqm ? `${u.size_sqm} m²` : <span className="text-slate-400">—</span>}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-3">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
                      <Wallet className="w-3 h-3" /> Preferred rate
                    </div>
                    <div className="font-mono-plex text-sm font-semibold text-slate-900 mt-1">
                      {u.preferred_rate ? formatCurrency(u.preferred_rate) : <span className="text-slate-400">—</span>}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Current tenant</div>
                  {occupied ? (
                    <div>
                      <div className="font-medium text-sm text-slate-900" data-testid={`pd-unit-tenant-${u.id}`}>{t.full_name}</div>
                      {t.monthly_rent > 0 && (
                        <div className="text-xs text-slate-500 mt-0.5 font-mono-plex">{formatCurrency(t.monthly_rent)} /mo</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 italic" data-testid={`pd-unit-vacant-${u.id}`}>
                      Vacant — allocate a tenant to start earning
                    </div>
                  )}
                </div>

                <UnitOccupancyHistory unitId={u.id} entries={history} currentTenantId={t ? t.id : null} />

                {u.notes && (
                  <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">{u.notes}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function UnitOccupancyHistory({ unitId, entries, currentTenantId }) {
  const [open, setOpen] = useState(false);
  const past = (entries || []).filter((t) => t.id !== currentTenantId);
  if (past.length === 0) {
    return (
      <div className="text-[10px] uppercase tracking-widest text-slate-400 border-t border-slate-100 pt-3" data-testid={`pd-unit-history-empty-${unitId}`}>
        No previous tenants
      </div>
    );
  }
  return (
    <div className="border-t border-slate-100 pt-3" data-testid={`pd-unit-history-${unitId}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`pd-unit-history-toggle-${unitId}`}
        className="flex items-center justify-between w-full text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-900 transition"
      >
        <span className="inline-flex items-center gap-1.5">
          <History className="w-3 h-3" /> Occupancy history · {past.length} previous
        </span>
        <span className={`transition-transform ${open ? "rotate-90" : ""}`}>›</span>
      </button>
      {open && (
        <ol className="mt-2 space-y-2 relative border-l border-slate-200 pl-3 ml-1">
          {past.map((t) => (
            <li key={t.id} className="text-xs" data-testid={`pd-unit-history-entry-${t.id}`}>
              <span className="absolute -left-[5px] w-2 h-2 rounded-full bg-slate-400 mt-1.5"></span>
              <div className="font-medium text-slate-900">{t.full_name}</div>
              <div className="text-slate-500 font-mono-plex text-[11px] mt-0.5">
                {t.start_date ? formatDate(t.start_date) : "—"} → {t.end_date ? formatDate(t.end_date) : "—"}
                {t.monthly_rent > 0 && <> · {formatCurrency(t.monthly_rent)}/mo</>}
              </div>
              {(t.move_out_details?.move_out_date) && (
                <div className="text-[10px] text-slate-400 mt-0.5">Moved out {formatDate(t.move_out_details.move_out_date)}</div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* ============================================================================
 * SECTION: Electricity
 * ============================================================================ */
function ElectricitySection({ units, entries, summary, onAdd, onDelete }) {
  const unitName = (uid) => (units.find((u) => u.id === uid)?.name) || "—";
  const reconciled = summary ? Math.abs(summary.net) < 0.5 : false;
  return (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Electricity recovery</div>
          <div className="font-display text-xl font-semibold mt-0.5">Buy from supplier · Recover from units</div>
        </div>
        <button
          onClick={onAdd}
          data-testid="pd-electricity-add-btn"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition"
        >
          <Plus className="w-4 h-4" /> Log entry
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <SummaryCell
          label="Purchased (expense)"
          value={formatCurrency(summary?.total_purchased || 0)}
          sub={summary?.kwh_purchased ? `${summary.kwh_purchased.toFixed(0)} kWh` : ""}
          tone="red"
          testid="elec-purchased"
        />
        <SummaryCell
          label="Recovered (income)"
          value={formatCurrency(summary?.total_recovered || 0)}
          sub={summary?.kwh_recovered ? `${summary.kwh_recovered.toFixed(0)} kWh` : ""}
          tone="emerald"
          testid="elec-recovered"
        />
        <SummaryCell
          label={summary && summary.net >= 0 ? "Net gain" : "Net shortfall"}
          value={formatCurrency(Math.abs(summary?.net || 0))}
          sub={reconciled ? "Reconciled ✓" : (summary && summary.net < 0 ? "Under-recovered" : "Over-recovered")}
          tone={summary && summary.net >= 0 ? "emerald" : "red"}
          testid="elec-net"
        />
        <SummaryCell
          label="kWh unreconciled"
          value={summary ? `${summary.kwh_unreconciled.toFixed(0)} kWh` : "—"}
          sub={summary && summary.kwh_purchased > 0 ? `${((summary.kwh_recovered / summary.kwh_purchased) * 100).toFixed(0)}% recovered` : ""}
          tone="slate"
          testid="elec-kwh-diff"
        />
      </div>

      {/* Per-unit breakdown */}
      {summary && summary.per_unit && summary.per_unit.length > 0 && (
        <div className="card-flat overflow-hidden mb-6 fade-up">
          <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-600" />
            <div className="text-xs uppercase tracking-widest text-slate-500">Recovery by unit</div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-6 py-2.5 font-medium">Unit</th>
                <th className="text-right px-6 py-2.5 font-medium">Entries</th>
                <th className="text-right px-6 py-2.5 font-medium">kWh</th>
                <th className="text-right px-6 py-2.5 font-medium">Recovered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summary.per_unit.map((row) => (
                <tr key={row.unit_id} data-testid={`elec-per-unit-${row.unit_id}`}>
                  <td className="px-6 py-3 font-medium">{row.unit_name}</td>
                  <td className="px-6 py-3 text-right font-mono-plex text-slate-500">{row.entries}</td>
                  <td className="px-6 py-3 text-right font-mono-plex text-slate-700">{row.kwh.toFixed(0)}</td>
                  <td className="px-6 py-3 text-right font-mono-plex font-medium text-emerald-700">{formatCurrency(row.recovered)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* All entries */}
      {entries.length === 0 ? (
        <div className="card-flat p-8 text-center fade-up">
          <Zap className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold">No electricity entries yet</div>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Log a purchase when you buy prepaid electricity from the supplier, and a recovery when you sell / re-charge it to a unit.
          </p>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto fade-up">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Date</th>
                <th className="text-left px-6 py-3 font-medium">Type</th>
                <th className="text-left px-6 py-3 font-medium">Unit / Supplier</th>
                <th className="text-right px-6 py-3 font-medium">kWh</th>
                <th className="text-right px-6 py-3 font-medium">Amount</th>
                <th className="text-left px-6 py-3 font-medium">Notes</th>
                <th className="text-right px-6 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((e) => {
                const purchase = e.txn_type === "purchase";
                return (
                  <tr key={e.id} className="hover:bg-slate-50/50" data-testid={`elec-row-${e.id}`}>
                    <td className="px-6 py-3 font-mono-plex text-slate-700">{formatDate(e.date)}</td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md font-medium ${purchase ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {purchase ? "Purchase" : "Recovery"}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-slate-700">
                      {purchase ? (e.counterparty || <span className="text-slate-400">Supplier</span>) : (
                        <>
                          <div className="font-medium">{unitName(e.unit_id)}</div>
                          {e.counterparty && <div className="text-xs text-slate-500 mt-0.5">{e.counterparty}</div>}
                        </>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right font-mono-plex text-slate-500">{e.kwh ? Number(e.kwh).toFixed(0) : "—"}</td>
                    <td className={`px-6 py-3 text-right font-mono-plex font-medium ${purchase ? "text-red-700" : "text-emerald-700"}`}>
                      {purchase ? "−" : "+"}{formatCurrency(e.amount)}
                    </td>
                    <td className="px-6 py-3 text-xs text-slate-500 max-w-[240px] truncate">{e.notes || ""}</td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => onDelete(e.id)}
                        data-testid={`elec-delete-${e.id}`}
                        className="text-slate-400 hover:text-red-600 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function SummaryCell({ label, value, sub, tone = "slate", testid }) {
  const color = tone === "emerald" ? "text-emerald-700" : tone === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-semibold mt-1.5 ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1 font-mono-plex">{sub}</div>}
    </div>
  );
}

function TenantFormModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", id_number: "", emergency_contact: "", notes: "", deposit_holder: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/tenants", form);
      onSaved(data);
    } catch { toast.error("Could not save tenant"); }
  };
  return (
    <Modal onClose={onClose} title="Add tenant">
      <form onSubmit={save} className="space-y-4" data-testid="pd-tenant-form">
        <Field label="Full name">
          <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="pd-tenant-name" className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pd-tenant-email" className={inputCls} /></Field>
          <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="pd-tenant-phone" className={inputCls} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="ID / Passport"><input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className={inputCls} /></Field>
          <Field label="Emergency contact"><input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} className={inputCls} /></Field>
        </div>
        <Field label="Deposit held with (bank/agent/attorney)">
          <input value={form.deposit_holder} onChange={(e) => setForm({ ...form, deposit_holder: e.target.value })} data-testid="pd-tenant-deposit-holder" className={inputCls} placeholder="e.g. Standard Bank — Trust Account 12345" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancel</button>
          <button type="submit" data-testid="pd-tenant-save" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Save tenant</button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================================
 * SECTION: Units
 * ============================================================================ */

function UnitFormModal({ propertyId, initial, onClose, onSaved }) {
  const editing = !!(initial && initial.id);
  const [form, setForm] = useState({
    name: initial?.name || "",
    size_sqm: initial?.size_sqm || "",
    preferred_rate: initial?.preferred_rate || "",
    bedrooms: initial?.bedrooms || "",
    bathrooms: initial?.bathrooms || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name / number is required"); return; }
    setBusy(true);
    try {
      const payload = {
        property_id: propertyId,
        name: form.name.trim(),
        size_sqm: parseFloat(form.size_sqm) || 0,
        preferred_rate: parseFloat(form.preferred_rate) || 0,
        bedrooms: parseInt(form.bedrooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        notes: form.notes || "",
      };
      if (editing) {
        await api.put(`/units/${initial.id}`, payload);
        toast.success("Unit updated");
      } else {
        await api.post("/units", payload);
        toast.success("Unit added");
      }
      onSaved();
    } catch {
      toast.error("Could not save unit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={editing ? "Edit unit" : "Add unit"}>
      <form onSubmit={save} className="space-y-4" data-testid="pd-unit-form">
        <Field label="Name or number">
          <input
            required
            autoFocus
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="pd-unit-name"
            className={inputCls}
            placeholder="Unit 3B / Flat 12 / Cottage"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Size (m²)">
            <input
              type="number" step="0.01" min="0"
              value={form.size_sqm}
              onChange={(e) => setForm({ ...form, size_sqm: e.target.value })}
              data-testid="pd-unit-size"
              className={inputCls}
              placeholder="65"
            />
          </Field>
          <Field label="Preferred rate (R / month)">
            <input
              type="number" step="0.01" min="0"
              value={form.preferred_rate}
              onChange={(e) => setForm({ ...form, preferred_rate: e.target.value })}
              data-testid="pd-unit-rate"
              className={inputCls}
              placeholder="12000"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Bedrooms">
            <input
              type="number" min="0"
              value={form.bedrooms}
              onChange={(e) => setForm({ ...form, bedrooms: e.target.value })}
              className={inputCls}
              placeholder="2"
            />
          </Field>
          <Field label="Bathrooms">
            <input
              type="number" min="0"
              value={form.bathrooms}
              onChange={(e) => setForm({ ...form, bathrooms: e.target.value })}
              className={inputCls}
              placeholder="1"
            />
          </Field>
        </div>
        <Field label="Notes">
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className={`${inputCls} h-20 py-2 resize-none`}
            placeholder="Balcony, parking bay, north-facing…"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            data-testid="pd-unit-save"
            className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60 inline-flex items-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? "Save changes" : "Save unit"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
